import { isSafeLinkUrl, parseRich, serializeRich, RICH_SIZE_EM, RICH_SIZES, type RichAlign, type RichBlock, type RichSize, type RichSpan, type TextAlign } from '@survey/core';
import { AlignCenter, AlignJustify, AlignLeft, AlignRight, Bold, Eraser, Italic, Link2, List, ListOrdered, Underline } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ClipboardEvent, type FormEvent } from 'react';
import { useI18n } from '../i18n';

// A small rich text editor for question text. It edits real formatting in the
// page (contentEditable) and hands back the safe markup from rich.ts, never
// HTML: what is typed is read back from the DOM one element at a time, and
// anything that is not bold, italic, underline, a web link, a list or a
// paragraph is dropped.

type Format = Pick<RichSpan, 'bold' | 'italic' | 'underline' | 'href'>;

/** Never read for text: they are not content the person typed. */
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'TEMPLATE']);
const BLOCK_TAGS = new Set(['DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE', 'LI', 'TABLE', 'TR']);

function addSpan(out: RichSpan[], text: string, f: Format) {
  if (!text) return;
  const last = out[out.length - 1];
  if (last && !!last.bold === !!f.bold && !!last.italic === !!f.italic && !!last.underline === !!f.underline && last.href === f.href) last.text += text;
  else out.push({ text, ...(f.bold && { bold: true as const }), ...(f.italic && { italic: true as const }), ...(f.underline && { underline: true as const }), ...(f.href && { href: f.href }) });
}

/** The formatting an element adds: by tag, and by inline style (some browsers use style="font-weight: bold"). */
function formatOf(el: HTMLElement, f: Format): Format {
  const next = { ...f };
  const tag = el.tagName;
  if (tag === 'B' || tag === 'STRONG') next.bold = true;
  if (tag === 'I' || tag === 'EM') next.italic = true;
  if (tag === 'U') next.underline = true;
  if (tag === 'A') {
    const href = el.getAttribute('href') ?? '';
    if (isSafeLinkUrl(href)) next.href = href;
  }
  const st = el.style;
  if (st) {
    if (st.fontWeight === 'bold' || Number(st.fontWeight) >= 600) next.bold = true;
    if (st.fontStyle === 'italic') next.italic = true;
    if ((st.textDecorationLine || st.textDecoration || '').includes('underline')) next.underline = true;
  }
  return next;
}

function trimLines(spans: RichSpan[]): RichSpan[] {
  const out = spans.map((s) => ({ ...s }));
  while (out.length && !out[0]!.text.replace(/^\n+/, '')) out.shift();
  if (out.length) out[0]!.text = out[0]!.text.replace(/^\n+/, '');
  while (out.length && !out[out.length - 1]!.text.replace(/\n+$/, '')) out.pop();
  if (out.length) out[out.length - 1]!.text = out[out.length - 1]!.text.replace(/\n+$/, '');
  return out;
}
const hasText = (spans: RichSpan[]) => spans.some((s) => s.text.trim());

/** The alignment an element sets (style="text-align: …" or the old align="…" attribute). */
function alignOf(el: HTMLElement): RichAlign | undefined {
  const v = (el.style?.textAlign || el.getAttribute('align') || '').toLowerCase();
  if (v === 'center' || v === 'right' || v === 'justify' || v === 'left') return v;
  if (v === 'start') return 'left';
  if (v === 'end') return 'right';
  return undefined;
}

/** The size step an element carries (data-size, or the font-size the editor wrote). */
function sizeOf(el: HTMLElement): RichSize | undefined {
  const d = el.getAttribute('data-size');
  if (d === 'small' || d === 'large' || d === 'xlarge') return d;
  const fs = el.style?.fontSize;
  return RICH_SIZES.find((k) => RICH_SIZE_EM[k] === fs);
}

/** Reads the editor's content back as paragraphs and lists, each with the alignment and size it was given. */
export function domToBlocks(root: Node): RichBlock[] {
  const blocks: RichBlock[] = [];
  let cur: RichSpan[] = [];
  let align: RichAlign | undefined;
  let size: RichSize | undefined;
  const flush = () => {
    const spans = trimLines(cur);
    if (hasText(spans)) blocks.push({ kind: 'p', spans, ...(align ? { align } : {}), ...(size ? { size } : {}) });
    cur = [];
  };
  const inlineSpans = (node: Node, f: Format, out: RichSpan[]) => {
    if (node.nodeType === 3) return addSpan(out, (node.nodeValue ?? '').replace(/ /g, ' '), f);
    if (!(node instanceof HTMLElement)) return;
    if (SKIP_TAGS.has(node.tagName)) return;
    if (node.tagName === 'BR') return addSpan(out, '\n', f);
    if (node.tagName === 'UL' || node.tagName === 'OL') return; // lists nested in an item are flattened away
    const nf = formatOf(node, f);
    node.childNodes.forEach((c) => inlineSpans(c, nf, out));
  };
  const walk = (node: Node, f: Format) => {
    if (node.nodeType === 3) return addSpan(cur, (node.nodeValue ?? '').replace(/ /g, ' '), f);
    if (!(node instanceof HTMLElement)) return;
    const tag = node.tagName;
    if (SKIP_TAGS.has(tag)) return;
    if (tag === 'BR') return addSpan(cur, '\n', f);
    if (tag === 'UL' || tag === 'OL') {
      flush();
      const items = [...node.children]
        .filter((li) => li.tagName === 'LI')
        .map((li) => {
          const out: RichSpan[] = [];
          const lf = formatOf(li as HTMLElement, f);
          li.childNodes.forEach((c) => inlineSpans(c, lf, out));
          return trimLines(out).map((s) => ({ ...s, text: s.text.replace(/\n+/g, ' ') }));
        })
        .filter(hasText);
      const listAlign = alignOf(node) ?? align;
      const listSize = sizeOf(node) ?? size;
      if (items.length) blocks.push({ kind: tag === 'UL' ? 'ul' : 'ol', items, ...(listAlign ? { align: listAlign } : {}), ...(listSize ? { size: listSize } : {}) });
      return;
    }
    const nf = formatOf(node, f);
    if (BLOCK_TAGS.has(tag)) {
      flush();
      const outer = align;
      const outerSize = size;
      align = alignOf(node) ?? outer;
      size = sizeOf(node) ?? outerSize;
      const before = blocks.length;
      node.childNodes.forEach((c) => walk(c, nf));
      flush();
      // An empty line (a block with only a line break) is a blank line the person left on purpose.
      if (blocks.length === before && tag !== 'LI' && !(node.textContent ?? '').trim() && node.querySelector('br')) blocks.push({ kind: 'space' });
      align = outer;
      size = outerSize;
    } else node.childNodes.forEach((c) => walk(c, nf));
  };
  root.childNodes.forEach((c) => walk(c, {}));
  flush();
  // Blank lines before the first or after the last paragraph are nothing to keep.
  while (blocks[0]?.kind === 'space') blocks.shift();
  while (blocks[blocks.length - 1]?.kind === 'space') blocks.pop();
  return blocks;
}

/** Draws a block at a size step (or normal size with none). */
function setSize(el: HTMLElement, size: RichSize | undefined) {
  if (size) {
    el.setAttribute('data-size', size);
    el.style.fontSize = RICH_SIZE_EM[size];
  } else {
    el.removeAttribute('data-size');
    el.style.removeProperty('font-size');
  }
}

/** Builds the editable content for blocks: element by element, never from an HTML string. */
export function renderBlocks(root: HTMLElement, blocks: RichBlock[]) {
  root.replaceChildren();
  const doc = root.ownerDocument;
  const spanEl = (s: RichSpan): Node => {
    let node: Node = doc.createDocumentFragment();
    s.text.split('\n').forEach((line, i) => {
      if (i > 0) node.appendChild(doc.createElement('br'));
      node.appendChild(doc.createTextNode(line));
    });
    const wrap = (tag: string) => {
      const el = doc.createElement(tag);
      el.appendChild(node);
      node = el;
    };
    if (s.bold) wrap('strong');
    if (s.italic) wrap('em');
    if (s.underline) wrap('u');
    if (s.href && isSafeLinkUrl(s.href)) {
      const a = doc.createElement('a');
      a.setAttribute('href', s.href);
      a.appendChild(node);
      node = a;
    }
    return node;
  };
  for (const b of blocks) {
    if (b.kind === 'p') {
      const div = doc.createElement('div');
      if (b.align) div.style.textAlign = b.align;
      if (b.size) setSize(div, b.size);
      b.spans.forEach((s) => div.appendChild(spanEl(s)));
      root.appendChild(div);
    } else if (b.kind === 'space') {
      const div = doc.createElement('div');
      div.appendChild(doc.createElement('br'));
      root.appendChild(div);
    } else {
      const list = doc.createElement(b.kind);
      if (b.align) list.style.textAlign = b.align;
      if (b.size) setSize(list, b.size);
      for (const item of b.items) {
        const li = doc.createElement('li');
        item.forEach((s) => li.appendChild(spanEl(s)));
        list.appendChild(li);
      }
      root.appendChild(list);
    }
  }
}

/** Same content once read back, ignoring spaces at the ends that the owner may have trimmed. */
const sameText = (a: string, b: string) => a === b || serializeRich(parseRich(a)).trim() === serializeRich(parseRich(b)).trim();

const ALIGNS: { id: TextAlign; Icon: typeof AlignLeft }[] = [
  { id: 'left', Icon: AlignLeft },
  { id: 'center', Icon: AlignCenter },
  { id: 'right', Icon: AlignRight },
  { id: 'justify', Icon: AlignJustify },
];

type Command = 'bold' | 'italic' | 'underline' | 'insertUnorderedList' | 'insertOrderedList';

interface Props {
  /** The rich markup (see rich.ts). */
  value: string;
  onChange: (markup: string) => void;
  placeholder?: string;
  label: string;
  /** Smaller text for help text. */
  compact?: boolean;
  autoFocus?: boolean;
  /** Paragraph alignment of the text; with onAlign the toolbar offers left, centre, right and justify. */
  align?: TextAlign;
  /** `markup` is the text again when aligning everything cleared per-paragraph alignment, else undefined. */
  onAlign?: (align: TextAlign, markup?: string) => void;
}

export function RichEditor({ value, onChange, placeholder, label, compact, autoFocus, align, onAlign }: Props) {
  const { t } = useI18n();
  const el = useRef<HTMLDivElement>(null);
  const lastEmitted = useRef(value);
  const saved = useRef<Range | null>(null);
  const [active, setActive] = useState<Partial<Record<Command, boolean>>>({});
  const [linking, setLinking] = useState(false);
  const [url, setUrl] = useState('https://');
  const [urlError, setUrlError] = useState(false);

  // Put the content in the page when it changes from outside (another question picked, text edited elsewhere);
  // never while the person is typing, which would move the caret.
  useEffect(() => {
    const node = el.current;
    if (!node) return;
    if (value === lastEmitted.current && node.childNodes.length) return;
    // The builder stores plain text trimmed, so "Hello " comes back as "Hello": the same text to the
    // person typing, and rewriting it would throw the caret to the start (holding backspace or space).
    if (document.activeElement === node && sameText(value, lastEmitted.current)) return;
    renderBlocks(node, parseRich(value));
    lastEmitted.current = value;
  }, [value]);
  useEffect(() => {
    if (autoFocus) el.current?.focus();
  }, [autoFocus]);

  const emit = useCallback(() => {
    if (!el.current) return;
    const markup = serializeRich(domToBlocks(el.current));
    lastEmitted.current = markup;
    onChange(markup);
  }, [onChange]);

  const refreshActive = useCallback(() => {
    const q = (c: Command) => {
      try {
        return !!document.queryCommandState?.(c);
      } catch {
        return false;
      }
    };
    if (el.current && el.current.contains(document.getSelection()?.anchorNode ?? null)) {
      setActive({ bold: q('bold'), italic: q('italic'), underline: q('underline'), insertUnorderedList: q('insertUnorderedList'), insertOrderedList: q('insertOrderedList') });
    }
  }, []);
  useEffect(() => {
    document.addEventListener('selectionchange', refreshActive);
    return () => document.removeEventListener('selectionchange', refreshActive);
  }, [refreshActive]);

  const run = (command: string, arg?: string) => {
    el.current?.focus();
    document.execCommand?.('styleWithCSS', false, 'false');
    document.execCommand?.(command, false, arg);
    emit();
    refreshActive();
  };

  /**
   * With text selected, only the paragraphs it touches are aligned (a line inside a paragraph becomes its
   * own paragraph). With nothing selected, the whole text is aligned and the per-paragraph choices go.
   */
  const alignTo = (a: TextAlign) => {
    const node = el.current;
    if (!node) return;
    const sel = document.getSelection();
    const selected = !!sel && sel.rangeCount > 0 && !sel.isCollapsed && node.contains(sel.anchorNode) && node.contains(sel.focusNode);
    if (selected) {
      const command = { left: 'justifyLeft', center: 'justifyCenter', right: 'justifyRight', justify: 'justifyFull' }[a];
      document.execCommand?.('styleWithCSS', false, 'true');
      document.execCommand?.(command, false);
      emit();
      return;
    }
    const blocks = domToBlocks(node);
    if (!blocks.some((b) => b.align)) return onAlign?.(a);
    // One change, so the alignment and the text are saved together.
    const plain = blocks.map(({ align: _a, ...b }) => b as RichBlock);
    renderBlocks(node, plain);
    const markup = serializeRich(plain);
    lastEmitted.current = markup;
    onAlign?.(a, markup);
  };

  /**
   * Bigger or smaller text: only the paragraphs a selection touches, or all of them when nothing is selected.
   * Each press moves the paragraphs one step (small, normal, large, extra large).
   */
  const stepSize = (delta: 1 | -1) => {
    const node = el.current;
    if (!node) return;
    // Loose text at the top level becomes a paragraph of its own, so it can be sized.
    let loose: HTMLElement | null = null;
    for (const child of [...node.childNodes]) {
      const block = child instanceof HTMLElement && (BLOCK_TAGS.has(child.tagName) || child.tagName === 'UL' || child.tagName === 'OL');
      if (block) {
        loose = null;
      } else if (child.nodeType === 3 || (child instanceof HTMLElement && child.tagName !== 'BR')) {
        if (!loose) {
          loose = node.ownerDocument.createElement('div');
          node.insertBefore(loose, child);
        }
        loose.appendChild(child);
      } else if (loose) loose.appendChild(child);
    }
    const sel = document.getSelection();
    const range = sel && sel.rangeCount > 0 && !sel.isCollapsed && node.contains(sel.anchorNode) && node.contains(sel.focusNode) ? sel.getRangeAt(0) : null;
    const targets = ([...node.children] as HTMLElement[]).filter((c) => !range || range.intersectsNode(c));
    for (const t of targets) {
      const order: (RichSize | undefined)[] = ['small', undefined, 'large', 'xlarge'];
      const at = order.indexOf(sizeOf(t));
      const next = order[Math.min(order.length - 1, Math.max(0, at + delta))];
      setSize(t, next);
    }
    emit();
  };

  const onPaste = (e: ClipboardEvent) => {
    // Pasted text keeps no formatting from wherever it came from; formatting is added with the toolbar.
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    if (text) run('insertText', text);
  };

  const openLink = () => {
    const sel = document.getSelection();
    saved.current = sel && sel.rangeCount && el.current?.contains(sel.anchorNode) ? sel.getRangeAt(0).cloneRange() : null;
    setLinking(true);
    setUrlError(false);
  };
  const applyLink = (e: FormEvent) => {
    e.preventDefault();
    const clean = url.trim();
    if (!isSafeLinkUrl(clean)) return setUrlError(true);
    el.current?.focus();
    const sel = document.getSelection();
    if (saved.current && sel) {
      sel.removeAllRanges();
      sel.addRange(saved.current);
    }
    run('createLink', clean);
    setLinking(false);
    setUrl('https://');
  };

  const btn = (command: Command, icon: React.ReactNode, name: string) => (
    <button key={command} type="button" className={`rich-btn${active[command] ? ' on' : ''}`} aria-label={name} aria-pressed={!!active[command]} title={name} onMouseDown={(e) => e.preventDefault()} onClick={() => run(command)}>
      {icon}
    </button>
  );

  return (
    <div className={`rich-editor${compact ? ' compact' : ''}`}>
      <div className="rich-toolbar" role="toolbar" aria-label={t('rich.toolbar')}>
        {btn('bold', <Bold />, t('rich.bold'))}
        {btn('italic', <Italic />, t('rich.italic'))}
        {btn('underline', <Underline />, t('rich.underline'))}
        <span className="rich-sep" aria-hidden />
        <button type="button" className="rich-btn rich-size" aria-label={t('rich.size_down')} title={t('rich.size_down')} onMouseDown={(e) => e.preventDefault()} onClick={() => stepSize(-1)}>
          <span aria-hidden>A<small>−</small></span>
        </button>
        <button type="button" className="rich-btn rich-size" aria-label={t('rich.size_up')} title={t('rich.size_up')} onMouseDown={(e) => e.preventDefault()} onClick={() => stepSize(1)}>
          <span aria-hidden>A<small>+</small></span>
        </button>
        <span className="rich-sep" aria-hidden />
        {btn('insertUnorderedList', <List />, t('rich.bullets'))}
        {btn('insertOrderedList', <ListOrdered />, t('rich.numbers'))}
        <span className="rich-sep" aria-hidden />
        <button type="button" className="rich-btn" aria-label={t('rich.link')} title={t('rich.link')} onMouseDown={(e) => e.preventDefault()} onClick={openLink}>
          <Link2 />
        </button>
        <button
          type="button"
          className="rich-btn"
          aria-label={t('rich.clear')}
          title={t('rich.clear')}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            run('removeFormat');
            run('unlink');
          }}
        >
          <Eraser />
        </button>
        {onAlign && (
          <>
            <span className="rich-sep" aria-hidden />
            {ALIGNS.map(({ id, Icon }) => (
              <button
                key={id}
                type="button"
                className={`rich-btn${(align ?? 'left') === id ? ' on' : ''}`}
                aria-pressed={(align ?? 'left') === id}
                aria-label={t(`rich.align_${id}`)}
                title={t(`rich.align_${id}`)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => alignTo(id)}
              >
                <Icon />
              </button>
            ))}
          </>
        )}
      </div>
      {linking && (
        <form className="rich-link" onSubmit={applyLink}>
          <input className="input sm" autoFocus value={url} aria-label={t('rich.link_url')} aria-invalid={urlError} onChange={(e) => { setUrl(e.target.value); setUrlError(false); }} />
          <button className="btn sm primary">{t('rich.apply')}</button>
          <button type="button" className="btn sm" onClick={() => setLinking(false)}>
            {t('common.cancel')}
          </button>
          {urlError && <span className="error-text">{t('rich.link_invalid')}</span>}
        </form>
      )}
      <div
        ref={el}
        className="rich-area"
        style={align && align !== 'left' ? { textAlign: align } : undefined}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={label}
        data-placeholder={placeholder}
        spellCheck
        onInput={emit}
        onPaste={onPaste}
        onBlur={emit}
        onKeyUp={refreshActive}
        onMouseUp={refreshActive}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onDrop={(e) => e.preventDefault()}
      />
    </div>
  );
}
